/** Canonical calendar event persistence for public.calendar_events. */
import { supabase, isDemoMode } from '../../../lib/supabase';
import type { CalendarEvent, ExternalSyncInfo } from '../types';
import { cancelGoogleCalendarEvent } from '../../calls/providers/googleMeetAdapter';
import { cancelOutlookEvent } from '../../calls/providers/microsoftTeamsAdapter';
import { endCall } from '../../calls/callService';

function getClient() {
  return isDemoMode ? null : supabase;
}

type CanonicalEventType = 'meeting' | 'milestone' | 'task' | 'deadline' | 'review';
type CanonicalEventStatus = 'tentative' | 'confirmed' | 'cancelled';

export interface CalendarEventRow {
  id: string;
  workspace_id: string;
  project_id: string | null;
  milestone_id: string | null;
  task_id: string | null;
  title: string;
  description: string;
  event_type: CanonicalEventType;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  timezone: string;
  location: string | null;
  meeting_url: string | null;
  provider: string | null;
  external_event_id: string | null;
  attendees: unknown;
  status: CanonicalEventStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type CalendarEventInsert = Omit<CalendarEventRow, 'id' | 'created_at' | 'updated_at'>;
export type CalendarEventUpdate = Partial<Omit<
  CalendarEventRow,
  'id' | 'workspace_id' | 'created_by' | 'created_at' | 'updated_at'
>>;

export interface ServiceResult<T> {
  data: T | null;
  error: Error | null;
  success: boolean;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  error: Error | null;
}

export interface CalendarEventQueryOptions {
  projectId?: string;
  ownerId?: string;
  assigneeId?: string;
  eventType?: 'task' | 'milestone' | 'meeting' | 'project';
  status?: 'active' | 'completed' | 'cancelled';
  dateFrom?: Date;
  dateTo?: Date;
  externalProvider?: 'google' | 'microsoft';
  hasConflicts?: boolean;
  externalOriginOnly?: boolean;
  page?: number;
  pageSize?: number;
  /** Legacy view-model alias retained for callers; maps to starts_at. */
  sortBy?: 'event_date' | 'starts_at' | 'created_at' | 'updated_at' | 'title';
  sortDirection?: 'asc' | 'desc';
}

function dbType(type: CalendarEvent['type']): CanonicalEventType {
  return type === 'project' ? 'review' : type;
}

function domainType(type: CanonicalEventType): CalendarEvent['type'] {
  if (type === 'review') return 'project';
  if (type === 'deadline') return 'task';
  return type;
}

function dbStatus(status: CalendarEvent['status']): CanonicalEventStatus {
  if (status === 'cancelled') return 'cancelled';
  return 'confirmed';
}

function domainStatus(status: CanonicalEventStatus): CalendarEvent['status'] {
  if (status === 'cancelled') return 'cancelled';
  return 'active';
}

function databaseProvider(event: Partial<Omit<CalendarEvent, 'id'>>): string | null {
  return event.meetingProvider ?? event.externalSync?.provider ?? null;
}

function externalProvider(provider: string | null): 'google' | 'microsoft' | null {
  if (provider === 'google' || provider === 'google_meet') return 'google';
  if (provider === 'microsoft' || provider === 'microsoft_teams') return 'microsoft';
  return null;
}

function meetingProvider(provider: string | null): CalendarEvent['meetingProvider'] {
  if (provider === 'google' || provider === 'google_meet') return 'google_meet';
  if (provider === 'microsoft' || provider === 'microsoft_teams') return 'microsoft_teams';
  return provider === 'fallback' ? 'fallback' : undefined;
}

function startOfEvent(event: Pick<CalendarEvent, 'date' | 'time'>): Date {
  const start = new Date(event.date);
  if (event.time) {
    const [hours, minutes] = event.time.split(':').map(Number);
    start.setHours(hours || 0, minutes || 0, 0, 0);
  } else {
    start.setHours(0, 0, 0, 0);
  }
  return start;
}

function endOfEvent(start: Date, duration: number | undefined, allDay: boolean): Date {
  return new Date(start.getTime() + Math.max(0, duration ?? (allDay ? 1440 : 60)) * 60_000);
}

function attendees(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = value.filter((item): item is string => typeof item === 'string');
  return ids.length ? ids : undefined;
}

function rowToCalendarEvent(row: CalendarEventRow, linkedCallSessionId?: string): CalendarEvent {
  const start = new Date(row.starts_at);
  const end = new Date(row.ends_at);
  const syncProvider = externalProvider(row.provider);
  const externalSync: ExternalSyncInfo | undefined = syncProvider && row.external_event_id
    ? {
        provider: syncProvider,
        externalEventId: row.external_event_id,
        syncDirection: 'bidirectional',
        lastSyncedAt: row.updated_at,
        syncStatus: 'synced',
      }
    : undefined;

  return {
    id: row.id,
    title: row.title,
    description: row.description || undefined,
    type: domainType(row.event_type),
    date: start,
    time: row.all_day
      ? undefined
      : `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
    duration: Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000)),
    project: row.project_id ?? undefined,
    attendees: attendees(row.attendees),
    location: row.location ?? undefined,
    status: domainStatus(row.status),
    externalSync,
    meetingProvider: meetingProvider(row.provider),
    meetingJoinUrl: row.meeting_url ?? undefined,
    linkedCallSessionId,
    meetingExternalEventId: row.external_event_id ?? undefined,
    meetingAgenda: row.event_type === 'meeting' && row.description ? row.description : undefined,
    meetingSyncStatus: row.provider && row.meeting_url ? 'synced' : undefined,
  };
}

function eventInsert(
  event: Omit<CalendarEvent, 'id'>,
  createdBy: string,
  workspaceId: string,
): CalendarEventInsert {
  const start = startOfEvent(event);
  const allDay = !event.time;
  return {
    workspace_id: workspaceId,
    project_id: event.project ?? null,
    milestone_id: null,
    task_id: null,
    title: event.title,
    description: event.description ?? event.meetingAgenda ?? '',
    event_type: dbType(event.type),
    starts_at: start.toISOString(),
    ends_at: endOfEvent(start, event.duration, allDay).toISOString(),
    all_day: allDay,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    location: event.location ?? null,
    meeting_url: event.meetingJoinUrl ?? null,
    provider: databaseProvider(event),
    external_event_id: event.meetingExternalEventId ?? event.externalSync?.externalEventId ?? null,
    attendees: event.attendees ?? [],
    status: dbStatus(event.status),
    created_by: createdBy,
  };
}

function eventUpdate(
  update: Partial<Omit<CalendarEvent, 'id'>>,
  existing: CalendarEvent,
): CalendarEventUpdate {
  const row: CalendarEventUpdate = {};
  if (update.title !== undefined) row.title = update.title;
  if (update.description !== undefined || update.meetingAgenda !== undefined) {
    row.description = update.description ?? update.meetingAgenda ?? '';
  }
  if (update.type !== undefined) row.event_type = dbType(update.type);
  if (update.project !== undefined) row.project_id = update.project ?? null;
  if (update.attendees !== undefined) row.attendees = update.attendees ?? [];
  if (update.location !== undefined) row.location = update.location ?? null;
  if (update.status !== undefined) row.status = dbStatus(update.status);
  // Presence is significant for meeting metadata: callers intentionally pass
  // `undefined` when removing a provider. Persist that removal as SQL NULL
  // instead of treating it as an omitted patch field.
  if (Object.prototype.hasOwnProperty.call(update, 'meetingJoinUrl')) {
    row.meeting_url = update.meetingJoinUrl ?? null;
  }
  if (
    Object.prototype.hasOwnProperty.call(update, 'meetingProvider')
    || Object.prototype.hasOwnProperty.call(update, 'externalSync')
  ) {
    row.provider = databaseProvider(update);
  }
  if (
    Object.prototype.hasOwnProperty.call(update, 'meetingExternalEventId')
    || Object.prototype.hasOwnProperty.call(update, 'externalSync')
  ) {
    row.external_event_id = update.meetingExternalEventId ?? update.externalSync?.externalEventId ?? null;
  }
  if (update.date !== undefined || update.time !== undefined || update.duration !== undefined) {
    const merged = {
      date: update.date ?? existing.date,
      time: update.time !== undefined ? update.time : existing.time,
    };
    const start = startOfEvent(merged);
    const allDay = !merged.time;
    row.starts_at = start.toISOString();
    row.ends_at = endOfEvent(start, update.duration ?? existing.duration, allDay).toISOString();
    row.all_day = allDay;
  }
  return row;
}

function resultError<T>(message: string): ServiceResult<T> {
  return { data: null, error: new Error(message), success: false };
}

export class CalendarEventService {
  private static instance: CalendarEventService | null = null;

  private constructor() {}

  static getInstance(): CalendarEventService {
    if (!CalendarEventService.instance) CalendarEventService.instance = new CalendarEventService();
    return CalendarEventService.instance;
  }

  private async createScope(
    client: typeof supabase,
    projectId: string | undefined,
    requestedOwnerId: string,
  ): Promise<{ actorId: string; workspaceId: string }> {
    const { data: authData, error: authError } = await client.auth.getUser();
    const actorId = authData.user?.id;
    if (authError || !actorId) throw new Error(authError?.message ?? 'User not authenticated');
    if (actorId !== requestedOwnerId) throw new Error('Calendar events can only be created for the authenticated user');

    if (projectId) {
      const { data, error } = await client.from('projects').select('workspace_id').eq('id', projectId).single();
      if (error || !data?.workspace_id) {
        throw new Error(error?.message ?? 'Project is unavailable or outside your access scope');
      }
      return { actorId, workspaceId: String(data.workspace_id) };
    }

    const { data, error } = await client.from('users').select('workspace_id').eq('id', actorId).single();
    if (error || !data?.workspace_id) throw new Error(error?.message ?? 'Authenticated user has no active workspace');
    return { actorId, workspaceId: String(data.workspace_id) };
  }

  private async hydrate(client: typeof supabase, rows: CalendarEventRow[]): Promise<CalendarEvent[]> {
    if (!rows.length) return [];
    const { data, error } = await client
      .from('call_sessions')
      .select('id, calendar_event_id, created_at')
      .in('calendar_event_id', rows.map((row) => row.id))
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    const calls = new Map<string, string>();
    for (const call of data ?? []) {
      if (typeof call.calendar_event_id === 'string' && !calls.has(call.calendar_event_id)) {
        calls.set(call.calendar_event_id, String(call.id));
      }
    }
    return rows.map((row) => rowToCalendarEvent(row, calls.get(row.id)));
  }

  async createEvent(event: Omit<CalendarEvent, 'id'>, ownerId: string): Promise<ServiceResult<CalendarEvent>> {
    const client = getClient();
    if (!client) return resultError('Database not available in demo mode');
    try {
      const scope = await this.createScope(client, event.project, ownerId);
      const { data, error } = await client
        .from('calendar_events')
        .insert(eventInsert(event, scope.actorId, scope.workspaceId))
        .select()
        .single();
      if (error || !data) return resultError(error?.message ?? 'Calendar event was not created');
      const [mapped] = await this.hydrate(client, [data as CalendarEventRow]);
      return { data: mapped, error: null, success: true };
    } catch (error) {
      return resultError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getEventById(id: string): Promise<ServiceResult<CalendarEvent>> {
    const client = getClient();
    if (!client) return resultError('Database not available in demo mode');
    try {
      const { data, error } = await client.from('calendar_events').select('*').eq('id', id).single();
      if (error || !data) return resultError(error?.message ?? 'Calendar event not found');
      const [mapped] = await this.hydrate(client, [data as CalendarEventRow]);
      return { data: mapped, error: null, success: true };
    } catch (error) {
      return resultError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async getEvents(options: CalendarEventQueryOptions = {}): Promise<PaginatedResult<CalendarEvent>> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? 50;
    const client = getClient();
    if (!client) return { data: [], total: 0, page, pageSize, hasMore: false, error: new Error('Database not available in demo mode') };
    const unsupported = options.assigneeId ? 'assigneeId' : options.hasConflicts ? 'hasConflicts' : options.externalOriginOnly ? 'externalOriginOnly' : null;
    if (unsupported) {
      return { data: [], total: 0, page, pageSize, hasMore: false, error: new Error(`${unsupported} is not represented by the canonical calendar_events schema`) };
    }

    try {
      let query = client.from('calendar_events').select('*', { count: 'exact' });
      if (options.projectId) query = query.eq('project_id', options.projectId);
      if (options.ownerId) query = query.eq('created_by', options.ownerId);
      if (options.eventType) query = query.eq('event_type', dbType(options.eventType));
      if (options.status) query = query.eq('status', dbStatus(options.status));
      else query = query.neq('status', 'cancelled');
      if (options.dateFrom) query = query.gte('starts_at', options.dateFrom.toISOString());
      if (options.dateTo) {
        const through = new Date(options.dateTo);
        through.setHours(23, 59, 59, 999);
        query = query.lte('starts_at', through.toISOString());
      }
      if (options.externalProvider) {
        query = query.in('provider', options.externalProvider === 'google'
          ? ['google', 'google_meet']
          : ['microsoft', 'microsoft_teams']);
      }
      const sortBy = options.sortBy === 'event_date' || !options.sortBy ? 'starts_at' : options.sortBy;
      const from = (page - 1) * pageSize;
      const { data, error, count } = await query
        .order(sortBy, { ascending: (options.sortDirection ?? 'asc') === 'asc' })
        .range(from, from + pageSize - 1);
      if (error) return { data: [], total: 0, page, pageSize, hasMore: false, error: new Error(error.message) };
      const mapped = await this.hydrate(client, (data ?? []) as CalendarEventRow[]);
      const total = count ?? 0;
      return { data: mapped, total, page, pageSize, hasMore: from + mapped.length < total, error: null };
    } catch (error) {
      return { data: [], total: 0, page, pageSize, hasMore: false, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }

  async updateEvent(id: string, update: Partial<Omit<CalendarEvent, 'id'>>): Promise<ServiceResult<CalendarEvent>> {
    const client = getClient();
    if (!client) return resultError('Database not available in demo mode');
    try {
      const current = await this.getEventById(id);
      if (!current.success || !current.data) return current;
      const existing = current.data;

      if (update.status === 'cancelled' && existing.status !== 'cancelled' && existing.type === 'meeting') {
        if (existing.meetingProvider && existing.meetingExternalEventId) {
          try {
            if (existing.meetingProvider === 'google_meet') await cancelGoogleCalendarEvent(existing.meetingExternalEventId);
            if (existing.meetingProvider === 'microsoft_teams') await cancelOutlookEvent(existing.meetingExternalEventId);
          } catch (error) {
            console.warn('[CalendarEventService] Provider cancellation failed:', error);
          }
        }
        if (existing.linkedCallSessionId) {
          const closure = await endCall(existing.linkedCallSessionId);
          if (closure.error) return resultError(`Linked call cancellation failed: ${closure.error}`);
        }
      }

      const row = eventUpdate(update, existing);
      if (!Object.keys(row).length) return resultError('The requested fields are not represented by the canonical calendar_events schema');
      const { data, error } = await client.from('calendar_events').update(row).eq('id', id).select().single();
      if (error || !data) return resultError(error?.message ?? 'Calendar event was not updated');
      const [mapped] = await this.hydrate(client, [data as CalendarEventRow]);
      return { data: mapped, error: null, success: true };
    } catch (error) {
      return resultError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async deleteEvent(id: string): Promise<ServiceResult<boolean>> {
    const client = getClient();
    if (!client) return { data: false, error: new Error('Database not available in demo mode'), success: false };
    try {
      const current = await this.getEventById(id);
      if (!current.success || !current.data) {
        return { data: false, error: current.error ?? new Error('Calendar event not found'), success: false };
      }

      if (current.data.meetingProvider && current.data.meetingExternalEventId) {
        try {
          if (current.data.meetingProvider === 'google_meet') await cancelGoogleCalendarEvent(current.data.meetingExternalEventId);
          if (current.data.meetingProvider === 'microsoft_teams') await cancelOutlookEvent(current.data.meetingExternalEventId);
        } catch (error) {
          console.warn('[CalendarEventService] Provider cancellation failed:', error);
        }
      }

      const { data: linkedCalls, error: linkedCallsError } = await client
        .from('call_sessions')
        .select('id, status')
        .eq('calendar_event_id', id);
      if (linkedCallsError) {
        return { data: false, error: new Error(linkedCallsError.message), success: false };
      }

      let preserveCallHistory = false;
      for (const linkedCall of linkedCalls ?? []) {
        const status = String(linkedCall.status);
        if (status === 'active' || status === 'ended') preserveCallHistory = true;
        if (status !== 'ended') {
          const closure = await endCall(String(linkedCall.id));
          if (closure.error) {
            return { data: false, error: new Error(`Linked call cancellation failed: ${closure.error}`), success: false };
          }
        }
      }

      // Started calls retain their calendar anchor as an audit/history record.
      // Default calendar reads exclude cancelled rows, so this still behaves as
      // a user-visible deletion without orphaning the ended call session.
      if (preserveCallHistory) {
        const { error } = await client.from('calendar_events')
          .update({ status: 'cancelled' })
          .eq('id', id)
          .select('id')
          .single();
        if (error) return { data: false, error: new Error(error.message), success: false };
        return { data: true, error: null, success: true };
      }

      const { error } = await client.from('calendar_events').delete().eq('id', id);
      if (error) return { data: false, error: new Error(error.message), success: false };
      return { data: true, error: null, success: true };
    } catch (error) {
      return { data: false, error: error instanceof Error ? error : new Error('Unknown error'), success: false };
    }
  }

  async findByExternalSync(provider: 'google' | 'microsoft', externalEventId: string): Promise<ServiceResult<CalendarEvent>> {
    const client = getClient();
    if (!client) return resultError('Database not available in demo mode');
    try {
      const { data, error } = await client
        .from('calendar_events')
        .select('*')
        .in('provider', provider === 'google' ? ['google', 'google_meet'] : ['microsoft', 'microsoft_teams'])
        .eq('external_event_id', externalEventId)
        .maybeSingle();
      if (error) return resultError(error.message);
      if (!data) return { data: null, error: null, success: true };
      const [mapped] = await this.hydrate(client, [data as CalendarEventRow]);
      return { data: mapped, error: null, success: true };
    } catch (error) {
      return resultError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async applyPullSyncUpdate(
    id: string,
    update: Partial<Omit<CalendarEvent, 'id'>>,
    syncInfo?: ExternalSyncInfo,
    _sourceData?: Record<string, unknown>,
  ): Promise<ServiceResult<CalendarEvent>> {
    return this.updateEvent(id, syncInfo ? { ...update, externalSync: syncInfo } : update);
  }

  async updateExternalSync(id: string, syncInfo: ExternalSyncInfo): Promise<ServiceResult<CalendarEvent>> {
    return this.updateEvent(id, { externalSync: syncInfo });
  }

  async getEventsWithConflicts(): Promise<ServiceResult<CalendarEvent[]>> {
    return resultError('Calendar conflict metadata is not represented by the canonical calendar_events schema');
  }

  async getEventsPendingSync(_provider?: 'google' | 'microsoft'): Promise<ServiceResult<CalendarEvent[]>> {
    return resultError('Pending sync state is not represented by the canonical calendar_events schema');
  }

  async upsertFromExternalSource(event: Omit<CalendarEvent, 'id'>, ownerId: string): Promise<ServiceResult<CalendarEvent>> {
    if (!event.externalSync) return resultError('External sync info required for upsert');
    const current = await this.findByExternalSync(event.externalSync.provider, event.externalSync.externalEventId);
    if (!current.success) return current;
    return current.data ? this.updateEvent(current.data.id, event) : this.createEvent(event, ownerId);
  }

  async countLinkedEvents(provider: 'google' | 'microsoft'): Promise<ServiceResult<number>> {
    const client = getClient();
    if (!client) return { data: 3, error: null, success: true };
    try {
      const { count, error } = await client
        .from('calendar_events')
        .select('id', { count: 'exact', head: true })
        .in('provider', provider === 'google' ? ['google', 'google_meet'] : ['microsoft', 'microsoft_teams'])
        .not('external_event_id', 'is', null);
      if (error) return { data: 0, error: new Error(error.message), success: false };
      return { data: count ?? 0, error: null, success: true };
    } catch (error) {
      return { data: 0, error: error instanceof Error ? error : new Error('Unknown error'), success: false };
    }
  }

  async markProviderDisconnected(
    provider: 'google' | 'microsoft',
  ): Promise<ServiceResult<{ attempted: number; updated: number; failed: number; failedEventIds?: string[] }>> {
    const count = await this.countLinkedEvents(provider);
    if (!count.success) return { data: null, error: count.error, success: false };
    const attempted = count.data ?? 0;
    if (!attempted) return { data: { attempted: 0, updated: 0, failed: 0 }, error: null, success: true };
    return {
      data: { attempted, updated: 0, failed: attempted },
      error: new Error('Disconnected sync state is not represented by the canonical calendar_events schema'),
      success: false,
    };
  }

  async purgeProviderExternalMetadata(
    provider: 'google' | 'microsoft',
  ): Promise<ServiceResult<{ attempted: number; updated: number; failed: number; failedEventIds?: string[] }>> {
    const client = getClient();
    if (!client) return { data: { attempted: 0, updated: 0, failed: 0 }, error: null, success: true };
    try {
      const variants = provider === 'google' ? ['google', 'google_meet'] : ['microsoft', 'microsoft_teams'];
      const { data, error } = await client.from('calendar_events').select('id').in('provider', variants);
      if (error) return { data: null, error: new Error(error.message), success: false };
      const ids = (data ?? []).map((row) => String(row.id));
      if (!ids.length) return { data: { attempted: 0, updated: 0, failed: 0 }, error: null, success: true };
      const { error: updateError } = await client
        .from('calendar_events')
        .update({ provider: null, external_event_id: null })
        .in('id', ids);
      if (updateError) {
        return {
          data: { attempted: ids.length, updated: 0, failed: ids.length, failedEventIds: ids },
          error: new Error(updateError.message),
          success: false,
        };
      }
      return { data: { attempted: ids.length, updated: ids.length, failed: 0 }, error: null, success: true };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error'), success: false };
    }
  }
}

export function getCalendarEventService(): CalendarEventService {
  return CalendarEventService.getInstance();
}

export interface MarkDisconnectedResult {
  attempted: number;
  updated: number;
  failed: number;
  failedEventIds?: string[];
}

export async function markProviderEventsDisconnected(provider: 'google' | 'microsoft'): Promise<MarkDisconnectedResult> {
  const result = await CalendarEventService.getInstance().markProviderDisconnected(provider);
  return result.data ?? { attempted: 0, updated: 0, failed: 0 };
}

export async function countProviderLinkedEvents(provider: 'google' | 'microsoft'): Promise<number> {
  const result = await CalendarEventService.getInstance().countLinkedEvents(provider);
  return result.success ? result.data ?? 0 : 0;
}

export async function purgeProviderExternalMetadata(provider: 'google' | 'microsoft'): Promise<MarkDisconnectedResult> {
  const result = await CalendarEventService.getInstance().purgeProviderExternalMetadata(provider);
  return result.data ?? { attempted: 0, updated: 0, failed: 0 };
}

export default CalendarEventService;
